import { Injectable } from '@angular/core';
import { BaseCRMItem, CrmItemSnapshot } from '../../shared/models/crm/CRMItem.model';
import { DateTimeService, TextContentService } from '../services';
import { Domain } from '../../shared/models/domain.model';
import { Observable, of } from 'rxjs';
import { Note, TextContent, User } from '../../shared/models';
import { NOTE_TYPES } from '../../shared/models/crm/RecentNoteData.model';
import { finalize, shareReplay } from 'rxjs/operators';
import { CompanyNotesService } from './company';
import { BaseCRMServiceInt } from './base-crm-service.interface';
import { BannerNotificationsService, SanitizeService, SpinnerService } from '../../xform-compat';
import { HttpParams } from '@angular/common/http';
import { RmsColors } from '../../shared/constants/rms-colors';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root'
})
export class CrmUtilsService {

  private static TERMINAL_CUTOFF_DATE = 7 * 24 * 60 * 60 * 1000; // 7 days

  private companyNoteCache$: Record<string, Observable<Note>> = {};

  constructor(
    private companyNotesService: CompanyNotesService,
    private spinnerService: SpinnerService,
    private sanitizeService: SanitizeService,
    private textContentService: TextContentService,
    private bannerNotificationService: BannerNotificationsService,
  ) { }

  public getActiveCrmItems<T extends BaseCRMItem>(crmItems: Array<T>): Array<T> {
    return crmItems.filter(c => !c.archived);
  }

  public hasMultipleCrmItems<T extends BaseCRMItem>(crmItems: Array<T>): boolean {
    return this.getActiveCrmItems(crmItems).length > 1;
  }

  public getLatestActive<T extends BaseCRMItem>(crmItems: Array<T>): T {
    const active = this.getActiveCrmItems(crmItems)
      .sort((a, b) => a.lastActivity < b.lastActivity ? 1 : -1 );
    return active[0];
  }
  public hasArchivedItems<T extends BaseCRMItem>(crmItems: Array<T>): boolean {
    return crmItems.some(c => c.archived);
  }

  public isCrmProject<T extends BaseCRMItem>(crmItem: T): boolean {
    return crmItem.companies && crmItem.companies.length === 0;
  }

  public getDomainIconName(domain: Domain | string) {
    if (typeof domain === 'string') {
      return `${domain.toLowerCase().replace(/ /g, '-')}`;
    } else if (domain) {
      return `${domain.name.toLowerCase().replace(/ /g, '-')}`;
    }
  }

  public getWarningColor(created: DateTime | string) {
    if (!created) {
      return RmsColors.RMS_CRM_WARNING;
    }
    let estValue: DateTime;
    if (typeof created === 'string') {
      estValue = DateTimeService.parseDate(created).toLocal();
    } else {
      estValue = created;
    }
    if (Math.abs(estValue.diffNow('days').days) > 30) {
      return RmsColors.RMS_CRM_WARNING;
    } else if (Math.abs(estValue.diffNow('days').days) > 14) {
      return '#CE9C11';
    } else {
      return 'inherit';
    }
  }

  public getRecentOvnNote<T extends BaseCRMItem>(item: T): Observable<Note | {id: number, textContent: TextContent, itemSnapshot: CrmItemSnapshot}>  {
    if (this.hasOverviewNote(item)) {
      const companyOvnNote = item.recentNotes[NOTE_TYPES.COMPANY_NOTE_OVERVIEW];
      const crmOvnNote = item.recentNotes[NOTE_TYPES.CRM_NOTE_OVERVIEW];
      if ((companyOvnNote && !crmOvnNote) || (companyOvnNote && companyOvnNote.updated) > (crmOvnNote && crmOvnNote.updated)) {
        return this.getCompanyNotes(companyOvnNote.spaceId, companyOvnNote.containerId);
      }
      if (crmOvnNote) {
        return of(item.notes.find(n => n.id === item.recentNotes[NOTE_TYPES.CRM_NOTE_OVERVIEW].containerId));
      }
    }
    return of(null);
  }

  public hasOverviewNote<T extends BaseCRMItem>(item: T): boolean {
    return !!(item.recentNotes && (item.recentNotes[NOTE_TYPES.CRM_NOTE_OVERVIEW] ||
      item.recentNotes[NOTE_TYPES.COMPANY_NOTE_OVERVIEW]));
  }

  public getRecentCrmNote<T extends BaseCRMItem>(item: T): Observable<{
    id: number;
    textContent: TextContent;
    itemSnapshot: CrmItemSnapshot
  }> | Observable<Note> {
    return this.hasCrmNote(item) ? of(item.notes.find(n => n.id === item.recentNotes[NOTE_TYPES.CRM_NOTE].containerId)) : of(null);
  }

  public hasRecentNotes<T extends BaseCRMItem>(item: T): boolean {
    return this.hasOverviewNote(item) || this.hasCrmNote(item);
  }

  public hasCrmNote<T extends BaseCRMItem>(item: T): boolean {
    return !!(item.recentNotes && item.recentNotes[NOTE_TYPES.CRM_NOTE]);
  }

  public saveNewNote<T extends BaseCRMItem, S extends BaseCRMServiceInt>(crmItem: T, content: string, crmService: S, params?: any): void {
    if (!content) {
      return;
    }
    this.spinnerService.start();
    this.textContentService.checkAbsoluteImgSrc(content);
    crmService.createNote(crmItem.id, {
      content: this.sanitizeService.sanitizeNoteContent(content),
      isOverview: false
    }, params).pipe(
      finalize(() => {
        crmService.getRecord(crmItem.id).subscribe((resp: T) => {
          crmItem = resp;
          this.spinnerService.stop();
        }, () => {
          this.spinnerService.stop();
        });
      }))
      .subscribe(
        () => {},
        error1 => this.bannerNotificationService.error(error1)
      );
  }

  public containsUser(item: BaseCRMItem, user: User): boolean {
    return item.participants.map(p => p.user.id).includes(user.id);
  }

  public isDiligenceLead(item: BaseCRMItem, user: User): boolean {
    return item.leader && item.leader.user.id === user.id;
  }

  public setRequestParams(filter: any): HttpParams {
    if (filter) {
      let params = new HttpParams()
        .set('page', filter.page)
        .set('pagesize', filter.pagesize)
        .set('direction', filter.direction)
        .set('sort', filter.sort);

      if (filter.date) {
        params = params.set('date', filter.date);
      }
      if (filter.ownerId) {
        filter.ownerId.forEach(owner => params = params.append('ownerId', owner));
      }
      if (filter.leaderId) {
        filter.leaderId.forEach(leader => params = params.append('leaderId', leader));
      }
      if (filter.participantId) {
        filter.participantId.forEach(participant => params = params.append('participantId', participant));
      }
      if (filter.companyId instanceof Array) {
        filter.companyId.forEach(company => params = params.append('companyId', company));
      } else if (filter.companyId) {
        params = params.append('companyId', filter.companyId);
      }
      if (filter.domainId) {
        filter.domainId.forEach(domain => params = params.append('domainId', domain));
      }
      if (filter.userDomainId) {
        filter.userDomainId.forEach(domain => params = params.append('userDomainId', domain));
      }
      if (filter.status) {
        filter.status.forEach(status => params = params.append('status', status));
      }
      if (filter.notStatus) {
        filter.notStatus.forEach(status => params = params.append('notStatus', status));
      }
      if (filter.userPMStatus) {
        filter.userPMStatus.forEach(userPMStatus => params = params.append('userPMStatus', userPMStatus));
      }
      if (filter.userDLStatus) {
        filter.userDLStatus.forEach(userDLStatus => params = params.append('userDLStatus', userDLStatus));
      }
      if (filter.storm) {
        filter.storm.forEach(storm => params = params.append('storm', storm));
      }
      if (filter.courtshipType) {
        filter.courtshipType.forEach(courtshipType => params = params.append('courtshipType', courtshipType));
      }
      if (filter.userId === 0 || filter.userId) {
        params = params.set('userId', filter.userId);
      }
      if (filter.archived === true || filter.archived === false) {
        params = params.set('archived', filter.archived);
      }
      if (filter.nonDeal === true || filter.nonDeal === false) {
        params = params.set('nonDeal', filter.nonDeal);
      }
      if (filter.countOnly === true || filter.countOnly === false) {
        params = params.set('countOnly', filter.countOnly);
      }
      if (filter.includeSilenced === true || filter.includeSilenced === false) {
        params = params.set('includeSilenced', filter.includeSilenced);
      }
      if (filter.priorityMin !== null && filter.priorityMin !== undefined) {
        params = params.set('priorityMin', filter.priorityMin);
      }
      if (filter.priorityMax !== null && filter.priorityMax !== undefined) {
        params = params.set('priorityMax', filter.priorityMax);
      }
      if (filter.raDealSizeMin !== null && filter.raDealSizeMin !== undefined) {
        params = params.set('raDealSizeMin', filter.raDealSizeMin);
      }
      if (filter.raDealSizeMax !== null && filter.raDealSizeMax !== undefined) {
        params = params.set('raDealSizeMax', filter.raDealSizeMax);
      }
      if (filter.totalDealSizeMin !== null && filter.totalDealSizeMin !== undefined) {
        params = params.set('totalDealSizeMin', filter.totalDealSizeMin);
      }
      if (filter.totalDealSizeMax !== null && filter.totalDealSizeMax !== undefined) {
        params = params.set('totalDealSizeMax', filter.totalDealSizeMax);
      }
      return params;
    } else {
      return new HttpParams();
    }
  }

  public getDateForTerminalCutoff(): string {
    return new Date(Date.now() - CrmUtilsService.TERMINAL_CUTOFF_DATE).toISOString();
  }

  private getCompanyNotes(spaceId: number, containerId: number): Observable<Note> {
    const key = `${spaceId}-${containerId}`;
    if (!this.companyNoteCache$[key]) {
      this.companyNoteCache$[key] =  this.companyNotesService.getSpaceNote(spaceId, containerId)
        .pipe(shareReplay({ refCount: true, bufferSize: 1 }));
    }
    return this.companyNoteCache$[key];
  }

}
